import {observer} from "mobx-react";
import React from "react";
import AHref from "../../../components/ahref/ahref";
import  {FontBody2} from "../../../components/fonts/font-body2/font-body2";
import {FONT_COLOR, FONT_VARIANT} from "../../../components/fonts/font-props";
import IconDelete from "../../../components/icons/icon-delete/icon-delete";
import IconEdit from "../../../components/icons/icon-edit/icon-edit";
import IconSirenOnLight from "../../../components/icons/icon-siren-on-light/icon-siren-on-light";
import {ButtonIcon} from "../../../components/inputs/button-icon/button-icon";
import TableRow from "../../../components/table/components/table-row/table-row";
import Table from "../../../components/table/table";
import {IColumn} from "../../../components/table/table.types";
import TextTruncate from "../../../components/text-truncate/text-truncate";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import {rootContainer, TYPE} from "../../../services/container/root";
import IsMobileService from "../../../services/is-mobile-service/is-mobile-service";
import CreateRmaSettingsStore from "../stores/create-rma-settings-store";
import RequestProductStore from "../stores/request-product-store";
import './return-details-card.scss';
import RmaRequestStore from "../stores/rma-request-store";
import AddItemDrawer from "./add-item-drawer";
import Currency from "../../../components/currency/currency";
import {Note, NOTE_TYPE} from "../../../components/note/note";
import {
    getManagersApprovalReasonTranslationKeys, IManagersApprovalTranslations,
    MANAGERS_APPROVAL_REASONS
} from "../../manage-returns/rma-page/rma-types";
import Icon from "../../../components/icons/icon/icon";
import { Tooltip } from "../../../components/tooltip/tooltip";

interface IProps {
	rmaRequestStore: RmaRequestStore;
    createRmaSettingsStore: CreateRmaSettingsStore;
    /** the fulfillment's RMA already exists, so its items can no longer be edited (FR13) */
    isRMACreated?: boolean;
}

const ReturnDetailsSection: React.FC<IProps> = observer(({ rmaRequestStore, createRmaSettingsStore, isRMACreated = false }) => {
    
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-create-rma';
    const isIPad = rootContainer.get<IsMobileService>(TYPE.IsMobileService).isIPad();
    let tableColumns: IColumn[] = [
        { content: '', width: {value: 16, type: 'px'} },
        { content: getTranslation('IMAGE', pageScope), width: {value: 40, type: 'px'} },
        { content: getTranslation('NAME', pageScope), width: {value: 1, type: 'fr'} },
        { content: getTranslation('QUANTITY', pageScope), width: {value: 0.5, type: 'fr'} },
        { content: getTranslation('RETURN_REASON', pageScope), width: {value: 0.9, type: 'fr'} },
        { content: getTranslation('RESOLUTION', pageScope), width: {value: 0.8, type: 'fr'} },
        { content: getTranslation('RETURN_METHOD', pageScope), width: {value: 0.8, type: 'fr'}},
        { content: getTranslation('FEES', pageScope), width: {value: 0.5, type: 'fr'} },
        { content: getTranslation('ADJUSTMENTS', pageScope), width: {value: 0.7, type: 'fr'} },
        { content: getTranslation('BALANCE', pageScope), width: {value: 0.6, type: 'fr'} },
        { content: '', width: {value: 70, type: 'px'} }
    ];
    if(isIPad){
        tableColumns.splice(3,4);
    }

    const translationKeys = getManagersApprovalReasonTranslationKeys(MANAGERS_APPROVAL_REASONS.OUT_OF_POLICY) as IManagersApprovalTranslations;
	return (
        <>
            {
                !!rmaRequestStore.eligibilityStore.isOutOfPolicy &&
                <Note type={NOTE_TYPE.INFORMATION} body={
                    <FontBody2 color={FONT_COLOR.PRIMARY3}>
                        {
                            translationKeys?.noteKey?.key && translationKeys?.noteKey?.scope &&
                            getTranslation(translationKeys.noteKey.key, translationKeys.noteKey.scope)
                        }
                    </FontBody2>
                    }
                />
            }
            <Table columns={tableColumns}>
                {
                    !!rmaRequestStore.requestProducts.length &&
                    rmaRequestStore.requestProducts.map((itemStore: RequestProductStore) => {
                        let tableRow = [
                            itemStore.isGroupOutOfPolicy() ? 
                            <Tooltip
                                content={
                                    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                        {getTranslation('ITEM_OUT_OF_POLICY_INDICATOR_TEXT', pageScope)}
                                    </FontBody2>
                                }
                            >
                                <Icon Component={IconSirenOnLight} />
                            </Tooltip> : <></>,
                            <img alt="" className="product-image" src={itemStore.orderProductStore.imageUrl}/>,
                            <>
                                <div>
                                    <TextTruncate>
                                        <AHref href={itemStore.orderProductStore.variantUrl}
                                               target={'_blank'}>
                                            <FontBody2 color={FONT_COLOR.PRIMARY2}>{itemStore.orderProductStore.name}</FontBody2>
                                        </AHref>
                                    </TextTruncate>
                                </div>
                                <TextTruncate>
                                    <FontBody2 color={FONT_COLOR.NEUTRAL3}>{itemStore.orderProductStore.variantTitle}</FontBody2>
                                </TextTruncate>
                            </>,
                            <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                {itemStore.quantity}
                            </FontBody2>,
                            <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                {itemStore.reasonLabel}
                            </FontBody2>,
                            <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                {itemStore.resolutionLabel}
                            </FontBody2>,
                            <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                {itemStore.returnMethodLabel}
                            </FontBody2>,
                            <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                <Currency currencyCode={itemStore.paymentCurrencyCode!} amount={itemStore.restockingFeeSummary}/>
                            </FontBody2>,
                            <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                <Currency currencyCode={itemStore.paymentCurrencyCode!} amount={itemStore.adjustmentsSummary}/>
                            </FontBody2>,
                            <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                <Currency currencyCode={itemStore.paymentCurrencyCode!} amount={itemStore.balanceSummary}/>
                            </FontBody2>,
                            <div className='details-buttons'>
                            <ButtonIcon onClick={()=>{rmaRequestStore.removeProduct(itemStore);}} disabled={isRMACreated} color={'var(--color-accent-1)'} Component={IconDelete}></ButtonIcon>
                            <ButtonIcon onClick={()=>{itemStore.setEditMode(true);}} disabled={isRMACreated} Component={()=><IconEdit color={'var(--color-accent-1)'}/>}></ButtonIcon>
                            { itemStore.editMode &&
                                <AddItemDrawer
                                    createRmaSettingsStore={createRmaSettingsStore}
                                    closeAction={()=>{itemStore.setEditMode(false)}}
                                    request={rmaRequestStore}
                                    requestProduct={rmaRequestStore.getProductRequestToEdit(itemStore.orderProductStore, itemStore.requestModel())}>
                                </AddItemDrawer>
                                }
                            </div>
                        ];
                        if(isIPad){
                            tableRow.splice(3,4);
                        }
                           return <TableRow key={itemStore.uuid} cells={tableRow}
                            />
                        
                    })}
            </Table>
        </>
	);
});

export default ReturnDetailsSection;
